import React, { useRef, useMemo, useEffect } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import useSpiraleTorusVisualizer from "../hooks/useSpiraleTorusVisualizer";

/**
 * Rendu du Tore-Spirale — LE BACKEND POSSÈDE LA FORME ET LE MOUVEMENT.
 *
 * Voir docs/DOCTRINE_GEOMETRIE_CANONIQUE.md.
 *
 * ⚠️ Ce composant fabriquait sa propre entropie « dynamique » :
 *
 *   - il repartait des positions STATIQUES du hook, leur appliquait une
 *     rotation, puis prenait la différence entre points consécutifs comme
 *     « vitesses ». Or une rotation est une isométrie : la norme de ces
 *     différences est strictement INVARIANTE. Chaos éteint, la valeur produite
 *     était donc une CONSTANTE — la longueur d'arc de la courbe ;
 *   - il multipliait ensuite cette constante par `spiralSpeed`, `flashRate`,
 *     `bloomIntensity`, `bloomRadius` et `bloomThreshold`. Des effets de
 *     post-traitement entraient dans une entropie censée être physique ;
 *   - chaos allumé, il ajoutait `(Math.random() - 0.5) * 0.2` aux positions
 *     transmises, donc du bruit dans ce qui était présenté comme une mesure.
 *
 * Tout cela est remplacé par un rejeu : les frames viennent de `/animate`, les
 * vitesses sont MESURÉES sur le déplacement réel entre deux frames, et le
 * snapshot part vers `/dyn_entropy`. Le composant ne calcule plus aucune
 * position et ne pondère plus rien.
 *
 * Glow, flash et bloom restent — ce sont des effets de RENDU. Ils colorent et
 * font pulser, ils ne déplacent rien, et ils n'entrent dans aucun observable
 * (docs/ARBITRAGE_CHAOS_GLOW.md).
 */
export default function SpiralTorus3D({
  running = true,
  chaos = false,
  glow = true,
  spiralSpeedPct = 50,
  flashRatePct = 30,
  bloomIntensity = 1.2,
  bloomRadius = 0.6,
  bloomThreshold = 0.0,
  onSnapshot = () => {},
}) {
  const groupRef = useRef();
  const meshRefs = useRef([]);
  const playhead = useRef(0);
  const tRef = useRef(0);

  // Les curseurs qui agissent sur le MOTEUR sont transmis au hook, qui les
  // passe à `/animate`. Ceux qui agissent sur le RENDU restent ici.
  const { objects, nParBrin, lecteur } = useSpiraleTorusVisualizer({
    spiralSpeedPct,
    chaos,
  });

  // Géométries partagées : 200 objets, deux formes. Les recréer par objet
  // allouerait 200 buffers pour deux contenus distincts.
  const geometries = useMemo(() => {
    const taille = objects.length ? objects[0].size || 0.25 : 0.25;
    return {
      sphere: new THREE.SphereGeometry(taille, 12, 12),
      cube: new THREE.BoxGeometry(taille * 2, taille * 2, taille * 2),
    };
  }, [objects]);

  useEffect(() => () => {
    geometries.sphere.dispose();
    geometries.cube.dispose();
  }, [geometries]);

  // Couleurs CANONIQUES, reçues de `/initial`. Ce sont elles que le snapshot
  // remonte — pas les couleurs affichées, que `glow` réécrit à chaque frame.
  const couleurs = useMemo(() => objects.map((o) => o.color), [objects]);

  // Vitesses MESURÉES, dérivées du déplacement réel entre deux frames servies.
  const vitesses = useRef([]);
  useEffect(() => {
    vitesses.current = objects.map(() => [0, 0, 0]);
    meshRefs.current = meshRefs.current.slice(0, objects.length);
  }, [objects]);

  useFrame((_, delta) => {
    const dt = Math.min(delta, 0.05);
    tRef.current += dt;
    const t = tRef.current;

    if (running && lecteur) {
      // Rejeu : aucune position n'est calculée ici.
      playhead.current += dt;
      const vue = lecteur.lire(playhead.current);
      if (!vue) return;
      if (vue.bascule || vue.gel) playhead.current = 0;
      const { alpha, dt: frameDt } = vue;

      const A = vue.a.positions;
      const B = vue.b.positions;

      for (let i = 0; i < objects.length; i++) {
        const mesh = meshRefs.current[i];
        const a = A[i];
        const b = B[i];
        if (!mesh || !a || !b) continue;

        const nx = a[0] + (b[0] - a[0]) * alpha;
        const ny = a[1] + (b[1] - a[1]) * alpha;
        const nz = a[2] + (b[2] - a[2]) * alpha;

        vitesses.current[i] = [
          (b[0] - a[0]) / frameDt,
          (b[1] - a[1]) / frameDt,
          (b[2] - a[2]) / frameDt,
        ];
        mesh.position.set(nx, ny, nz);
      }
    }

    // --- Effets de RENDU : ils colorent et pulsent, ils ne déplacent rien ---
    if (glow || flashRatePct > 0) {
      const flash = 0.5 + 0.5 * Math.sin(t * 10 * (flashRatePct / 100));
      for (let i = 0; i < meshRefs.current.length; i++) {
        const mesh = meshRefs.current[i];
        if (!mesh) continue;
        const base = couleurs[i];
        if (glow) {
          mesh.material.color.setHSL((Math.sin(t * 0.5 + i * 0.05) + 1) / 2, 0.8, 0.55);
        } else if (base) {
          mesh.material.color.setRGB(base[0], base[1], base[2]);
        }
        mesh.material.emissiveIntensity =
          flashRatePct > 0 ? flash * (flashRatePct / 100) * 2.0 : 0;
      }
    }

    // --- Instantané de scène pour l'entropie dynamique ---
    // Positions AFFICHÉES, couleurs CANONIQUES, vitesses MESURÉES. C'est le
    // contrat que fixe l'adaptateur du banc de conformité
    // (test_conformite_geometrie.py:_snapshots_spiral_torus).
    if (objects.length) {
      onSnapshot({
        positions: meshRefs.current
          .filter(Boolean)
          .map((m) => [m.position.x, m.position.y, m.position.z]),
        colors: couleurs,
        velocities: vitesses.current,
        n_par_brin: nParBrin,
      });
    }
  });

  return (
    <>
      <group ref={groupRef}>
        {objects.map((o, i) => (
          <mesh
            key={i}
            ref={(el) => (meshRefs.current[i] = el)}
            position={o.position}
            geometry={o.type === "cube" ? geometries.cube : geometries.sphere}
          >
            <meshStandardMaterial
              color={new THREE.Color(o.color[0], o.color[1], o.color[2])}
              emissive={new THREE.Color(o.color[0], o.color[1], o.color[2])}
              emissiveIntensity={0.5}
            />
          </mesh>
        ))}
      </group>

      <EffectComposer>
        <Bloom
          intensity={bloomIntensity}
          radius={bloomRadius}
          threshold={bloomThreshold}
        />
      </EffectComposer>
    </>
  );
}
